import React, { useState, useCallback, useMemo, memo } from 'react';
import { Grid } from '@mui/material';
import { FormField, RadioButtonGroup } from '../shared/form';
import { MisdeliveryFormData } from "../../models/misDelivery";
import { checkPro } from '../../API/checkPro';
import { useMisdeliveryFormContext } from '../../contexts/MisdeliveryFormContext';
import { S_GRID_PROPS } from '../../constants';

// Component no longer needs props - uses context
interface PalletInformationSectionProps {}

// Constants
const INVALID_PRO_ERROR = 'Enter valid PRO number' as const;

// Helper functions
async function validateProNumber(pro: number, onFieldChange: (field: keyof MisdeliveryFormData, value: string) => void, setProCheckDError: (v: boolean) => void) {
  if (!pro) return;
  try {
    const checkProResponse = await checkPro(pro);
    const isValidResponse = checkProResponse && Object.keys(checkProResponse).length > 0;
    if (isValidResponse) {
      setProCheckDError(false);
      patchShipperConsignee(onFieldChange, checkProResponse);
    } else {
      setProCheckDError(true);
      clearShipperConsignee(onFieldChange);
    }
  } catch (error) {
    setProCheckDError(true);
    clearShipperConsignee(onFieldChange);
    if (process.env.NODE_ENV === 'development') {
      console.error('Error validating PRO number:', error);
    }
  }
}

function clearShipperConsignee(onFieldChange: (field: keyof MisdeliveryFormData, value: string) => void) {
  onFieldChange('palletShipperName', '');
  onFieldChange('palletConsigneeName', '');
}

function patchShipperConsignee(onFieldChange: (field: keyof MisdeliveryFormData, value: string) => void, checkProResponse: any) {
  onFieldChange('palletShipperName', checkProResponse?.shipper ?? '');
  onFieldChange('palletConsigneeName', checkProResponse?.consignee ?? '');
}

function getProFieldError(touched: any, errors: any, proCheckDError: boolean) {
  return (touched.misdeliveredPalletPro && !!errors.misdeliveredPalletPro) || proCheckDError;
}

function getProFieldHelperText(touched: any, errors: any, proCheckDError: boolean) {
  if (touched.misdeliveredPalletPro && errors.misdeliveredPalletPro) {
    return errors.misdeliveredPalletPro;
  }
  if (proCheckDError && !errors.misdeliveredPalletPro) {
    return INVALID_PRO_ERROR;
  }
  return undefined;
}


const PalletInformationSection: React.FC<PalletInformationSectionProps> = memo(() => {
  // Get form state from context
  const { formData, errors, touched, onFieldChange, onFieldBlur, disabled } = useMisdeliveryFormContext();
  
  const [proCheckDError, setProCheckDError] = useState<boolean>(false);

  const handleRadioChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange('hasMisdeliveredPalletPro', e.target.value);
  }, [onFieldChange]);

  const handleProChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (proCheckDError) setProCheckDError(false);
    onFieldChange('misdeliveredPalletPro', e.target.value);
  }, [proCheckDError, onFieldChange]);

  const handleProBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const proValue = Number(e.target.value.trim())
    if(Number.isNaN(proValue)) {
     return;
    }
    onFieldBlur?.('misdeliveredPalletPro');
    if (proValue) {
      validateProNumber(proValue, onFieldChange, setProCheckDError);
    } else {
      setProCheckDError(false);
      clearShipperConsignee(onFieldChange);
    }
  }, [onFieldBlur, onFieldChange]);

  const proFieldError = useMemo(() => getProFieldError(touched, errors, proCheckDError), [touched, errors, proCheckDError]);
  const proFieldHelperText = useMemo(() => getProFieldHelperText(touched, errors, proCheckDError), [touched, errors, proCheckDError]);

  return (
    <Grid container spacing={2} className="filter-margin">
      <RadioButtonGroup
        id="hasMisdeliveredPalletPro"
        label="​Is there a pro number on the misdelivered product?"
        value={formData.hasMisdeliveredPalletPro}
        onChange={handleRadioChange}
        onBlur={() => onFieldBlur?.('hasMisdeliveredPalletPro')}
        required
        disabled={disabled}
        error={
          !["Y", "N"].includes(formData.hasMisdeliveredPalletPro ?? "") && touched.hasMisdeliveredPalletPro && !!errors.hasMisdeliveredPalletPro
        }
        helperText={!["Y", "N"].includes(formData.hasMisdeliveredPalletPro ?? "") && touched.hasMisdeliveredPalletPro ? errors.hasMisdeliveredPalletPro : undefined}
        gridProps={S_GRID_PROPS}
      />

      {/* PRO Number Found on Pallet */}
      {formData.hasMisdeliveredPalletPro === 'Y' && (
        <>
          <FormField
            id="misdeliveredPalletPro"
            label="PRO #"
            value={formData.misdeliveredPalletPro}
            onChange={handleProChange}
            onBlur={handleProBlur}
            placeholder="Enter PRO Number"
            type="number"
            required
            maxLength={11}
            error={proFieldError}
            helperText={proFieldHelperText}
            gridProps={S_GRID_PROPS}
            disabled={disabled}
          />

          <FormField
            id="palletShipperName"
            label="Shipper Name"
            value={formData.palletShipperName}
            disabled
            gridProps={S_GRID_PROPS}
          />

          <FormField
            id="palletConsigneeName"
            label="Consignee Name"
            value={formData.palletConsigneeName}
            disabled
            gridProps={S_GRID_PROPS}
          />
        </>
      )}
    </Grid>
  );
});

PalletInformationSection.displayName = 'PalletInformationSection';

export default PalletInformationSection;